import { api } from "../scripts/api.js";

/*
   <app-class-xp> - XP and skills for one class (2026-10-16).
   Route: #class-xp/<classId>             the skill matrix
          #class-xp/<classId>/boards      weekly, season and squad boards
          #class-xp/<classId>/squads      set squads and positions
          #class-xp/<classId>/awards      (2026-10-21) the week's awards (top 3 + top squad), who moved up,
                                          coach awards (Friday practice, sprints, team wins, invitationals)
          #class-xp/<classId>/gear        (2026-10-21) gear tiers, TENTATIVE: who has earned what; tick handed out
          #class-xp/<classId>/daily       (2026-10-21) the daily challenge: today and the next 13 days; pin a pack for a day
          Awards and Gear are for the TEAM class only (class_table.xp_team); any Game Launcher class has the daily challenge.

   Design: Skill-Profile-XP-PLAN-2026-10-16.md (second brain, Add-In project), David's
   rulings 10/16. Server: routes/xp.js (GET matrix, GET board, PUT squads).

   The MATRIX is students down the side and the ten skill areas across, each cell the
   highest tier cleared and its medal (or Started / blank). Untested cells are where to
   point a student next; the Next up column is what the add-in is already suggesting to
   them. XP and medals are re-ranked by server/recompute-xp.js when the rules change.
*/

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const num = (n) => Number(n || 0).toLocaleString("en-US");
const STARS = { bronze: "", silver: " ★", gold: " ★★" };
const DOW = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const REASON = { shore_up: "Shore up", discover: "Discover", speed_up: "Speed up", climb: "Climb" };

class AppClassXp extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <section id="class-xp">
            <div class="page-head">
               <button type="button" id="cx-back">&larr; Class</button>
               <h1 id="cx-title">XP and skills</h1>
               <p class="sub">Every clean clear of a Game Launcher tab earns XP and a medal; the matrix shows each student's highest tier per skill area.</p>
            </div>
            <div class="pr-tabs" role="group" aria-label="View">
               <a class="pr-tab" data-tab="matrix">Skill matrix</a>
               <a class="pr-tab" data-tab="boards">Boards</a>
               <a class="pr-tab" data-tab="squads">Squads</a>
               <a class="pr-tab" data-tab="awards">Awards</a>
               <a class="pr-tab" data-tab="gear">Gear</a>
               <a class="pr-tab" data-tab="daily">Daily challenge</a>
            </div>
            <p class="msg" id="cx-msg"></p>
            <div id="cx-body"></div>
         </section>`;
      this.querySelector("#cx-back").addEventListener("click", () => {
         location.hash = this.classId ? `#class-detail/${this.classId}` : "#classes";
      });
      this.boardKind = "weekly";
   }

   async show(param) {
      const [c, sub] = String(param || "").split("/");
      const classId = Number(c);
      const body = this.querySelector("#cx-body");
      if (!Number.isInteger(classId)) { body.innerHTML = `<p class="empty">No class selected.</p>`; return; }
      this.classId = classId;
      this.tab = ["boards", "squads", "awards", "gear", "daily"].includes(sub) ? sub : "matrix";
      this.querySelectorAll(".pr-tab").forEach((a) => {
         a.href = `#class-xp/${classId}${a.dataset.tab === "matrix" ? "" : "/" + a.dataset.tab}`;
         a.classList.toggle("on", a.dataset.tab === this.tab);
      });
      body.innerHTML = `<p class="empty">Loading...</p>`;
      this.msg("");
      try {
         if (this.tab === "boards") return this.renderBoards(await this.get(`/classes/${classId}/xp/board`));
         if (this.tab === "awards") return await this.renderAwards();   // await: a failed load shows its error, not a spinner
         if (this.tab === "gear") return this.renderGear(await this.get(`/classes/${classId}/xp/gear`));
         if (this.tab === "daily") return this.renderDaily(await this.get(`/classes/${classId}/xp/daily`));
         const m = await this.get(`/classes/${classId}/xp/matrix`);
         if (this.tab === "squads") return this.renderSquads(m);
         return this.renderMatrix(m);
      } catch (e) {
         body.innerHTML = "";
         this.msg(`Could not load (${e.message}). If this is the first time, the XP migration may not have run yet.`, "err");
      }
   }

   async get(path) {
      const res = await api(path);
      const d = await res.json().catch(() => ({ ok: false, reason: `http_${res.status}` }));
      if (!d.ok) throw new Error(d.reason || "failed");
      return d;
   }
   msg(t, kind = "") { const m = this.querySelector("#cx-msg"); m.textContent = t; m.className = `msg${kind ? " " + kind : ""}`; }

   renderMatrix(m) {
      const body = this.querySelector("#cx-body");
      const rows = m.students.slice().sort((a, b) => (a.expert - b.expert) || b.xp - a.xp);
      const cell = (a) => a.status === "cleared"
         ? `<td class="cx-cell cx-${esc(a.medal)}" title="${esc(a.area)}: Tier ${a.tier}, ${esc(a.medal)}">T${a.tier}${STARS[a.medal] || ""}</td>`
         : a.status === "started" ? `<td class="cx-cell cx-started" title="${esc(a.area)}: tried, not cleared">Started</td>`
         : `<td class="cx-cell cx-untested" title="${esc(a.area)}: not tried"></td>`;
      body.innerHTML = `
         <p class="cx-legend"><span class="cx-chip cx-gold">T3 ★★ gold</span><span class="cx-chip cx-silver">T3 ★ silver</span>
            <span class="cx-chip cx-bronze">T3 bronze</span><span class="cx-chip cx-started">Started</span><span class="cx-chip cx-untested">not tried</span>
            <span class="cx-rules">rules ${esc(m.rules_version)}</span></p>
         <div class="cx-scroll"><table class="cd-table cx-table">
            <thead><tr><th>Student</th><th>Squad</th><th>Lv</th><th>XP</th><th>Placement</th><th>Medals</th>
               ${m.areas.map((a) => `<th class="cx-area">${esc(a)}</th>`).join("")}<th>Next up</th></tr></thead>
            <tbody>${rows.map((s) => `<tr class="${s.expert ? "cx-expert" : ""}">
               <td><div class="cx-name">${esc(s.name)}${s.expert ? ` <span class="cx-tag">expert</span>` : ""}</div><div class="cx-sub">${esc(s.email)}</div></td>
               <td>${esc(s.squad || "")}<div class="cx-sub">${esc(s.position || "")}</div></td>
               <td>${s.level}<div class="cx-sub">${esc(s.title)}</div></td>
               <td class="cx-num">${num(s.xp)}</td>
               <td class="cx-num">${s.placement_level || "-"}</td>
               <td class="cx-med">${s.medals.gold}★★ ${s.medals.silver}★ ${s.medals.bronze}●<div class="cx-sub">${s.clean_clears} of ${s.attempts} tabs</div></td>
               ${s.areas.map(cell).join("")}
               <td class="cx-next">${(s.next_up || []).slice(0, 1).map((n) => `<b>${esc(REASON[n.reason] || n.reason)}</b> ${esc(n.title)}<div class="cx-sub">${esc(n.why)}</div>`).join("")}</td>
            </tr>`).join("")}</tbody></table></div>
         ${rows.length ? "" : `<p class="empty">No students in this class yet.</p>`}`;
   }

   renderBoards(b) {
      const body = this.querySelector("#cx-body");
      const k = this.boardKind;
      const tabs = ["weekly", "season", "squads"].map((x) =>
         `<button type="button" class="cx-btab ${k === x ? "on" : ""}" data-k="${x}">${x === "weekly" ? "This week" : x === "season" ? esc(b.season_name || "Season") : "Squads"}</button>`).join("");
      let table;
      if (k === "squads") {
         table = `<table class="cd-table"><thead><tr><th>#</th><th>Squad</th><th>Members</th><th>This week</th><th>Season</th></tr></thead><tbody>
            ${b.squads.map((q) => `<tr><td>${q.rank}</td><td>${esc(q.squad)}</td><td>${q.members.map((x) => `${esc(x.name)}${x.position ? ` (${esc(x.position)})` : ""}`).join(", ")}</td>
               <td class="cx-num">${num(q.week_xp)}</td><td class="cx-num">${num(q.season_xp)}</td></tr>`).join("")}</tbody></table>
            ${b.squads.length ? "" : `<p class="empty">No squads yet: set them on the Squads tab.</p>`}`;
      } else {
         const rows = b[k];
         table = `<table class="cd-table"><thead><tr><th>#</th><th>Student</th><th>Level</th><th>${k === "weekly" ? "This week" : esc(b.season_name || "Season")}</th></tr></thead><tbody>
            ${rows.map((r) => `<tr><td>${r.rank}</td><td>${esc(r.name)}<div class="cx-sub">${esc(r.nickname)}</div></td><td>${r.level} ${esc(r.title)}</td>
               <td class="cx-num">${num(k === "weekly" ? r.week_xp : r.season_xp)}</td></tr>`).join("")}</tbody></table>`;
      }
      body.innerHTML = `<div class="cx-btabs">${tabs}</div>
         <p class="cx-note">Students see these boards in the add-in's Game Launcher, by nickname. The week starts ${DOW[b.week_start_dow ?? 1]} 00:00 Arizona time;
            the season board counts ${esc(b.season_name || "this semester")}, while levels count everything ever earned.</p>${table}`;
      body.querySelectorAll(".cx-btab").forEach((x) => x.addEventListener("click", () => { this.boardKind = x.dataset.k; this.renderBoards(b); }));
   }

   renderSquads(m) {
      const body = this.querySelector("#cx-body");
      const rows = m.students.filter((s) => !s.expert);
      const opt = (v, cur) => `<option value="${v}" ${v === (cur || "") ? "selected" : ""}>${v || "-"}</option>`;
      body.innerHTML = `
         <p class="cx-note">A squad is three players (Point, Molder, Closer). Positions weight each student's Next up by the ConCats Skill Map; squads make the squad board.</p>
         <table class="cd-table cx-squads"><thead><tr><th>Student</th><th>Squad</th><th>Position</th></tr></thead><tbody>
            ${rows.map((s) => `<tr data-email="${esc(s.email)}"><td>${esc(s.name)}<div class="cx-sub">${esc(s.email)}</div></td>
               <td><input class="cx-squad" type="text" maxlength="40" value="${esc(s.squad || "")}" placeholder="e.g. Squad 1"></td>
               <td><select class="cx-pos">${["", "Point", "Molder", "Closer"].map((v) => opt(v, s.position)).join("")}</select></td></tr>`).join("")}
         </tbody></table>
         <div class="pr-actions"><button type="button" id="cx-save">Save squads</button><span class="msg" id="cx-save-msg"></span></div>`;
      body.querySelector("#cx-save").addEventListener("click", async () => {
         const squads = [...body.querySelectorAll("tr[data-email]")].map((tr) => ({
            email: tr.dataset.email, squad: tr.querySelector(".cx-squad").value.trim() || null, position: tr.querySelector(".cx-pos").value || null }));
         const out = body.querySelector("#cx-save-msg");
         out.textContent = "Saving...";
         try {
            const res = await api(`/classes/${this.classId}/xp/squads`, { method: "PUT", body: JSON.stringify({ squads }) });
            const d = await res.json();
            out.textContent = d.ok ? `Saved (${d.updated} students).` : `Could not save (${d.reason}).`;
         } catch { out.textContent = "Could not save (offline)."; }
      });
   }
}

/* ---------- Awards (2026-10-21) ---------- */
AppClassXp.prototype.renderAwards = async function renderAwards(weekDay = this.weekDay || null) {
   const body = this.querySelector("#cx-body");
   const [a, w] = await Promise.all([this.get(`/classes/${this.classId}/xp/awards`),
      this.get(`/classes/${this.classId}/xp/weekly${weekDay ? `?week=${weekDay}` : ""}`).catch((e) => ({ ok: false, reason: e.message }))]);
   if (!a.installed) {
      body.innerHTML = `<p class="msg err">Awards are not installed on the server yet (migration 2026-10-21_xp-motivation.sql).</p>`;
      return;
   }
   if (!a.team) {
      body.innerHTML = `<p class="cx-note">Weekly awards, coach awards and gear are for the <b>team</b> class (ConCats). This class keeps XP, levels, boards,
            skill XP and the daily challenge, and none of it touches grades.</p>
         <div class="pr-actions"><button type="button" id="cx-team-on">Make this the team class</button><span class="msg" id="cx-team-msg"></span></div>`;
      body.querySelector("#cx-team-on").addEventListener("click", async () => {
         const out = body.querySelector("#cx-team-msg");
         try {
            const r = await api(`/classes/${this.classId}/xp/team`, { method: "PUT", body: JSON.stringify({ team: true }) });
            const d = await r.json();
            if (!d.ok) { out.textContent = `Not saved (${d.reason === "not_authorized" ? "the class's teacher sets this" : d.reason}).`; return; }
            await this.renderAwards();
         } catch { out.textContent = "Not saved (offline)."; }
      });
      return;
   }
   const shift = (d, n) => { const t = new Date(`${d}T12:00:00Z`); t.setUTCDate(t.getUTCDate() + n); return t.toISOString().slice(0, 10); };
   const wk = w.ok ? w.week : null;
   const weekHtml = !w.ok ? `<p class="msg err">Could not load the week (${esc(w.reason)}).</p>` : `
      <div class="cx-weekbar"><button type="button" id="cx-wk-prev" title="Previous week">&larr;</button>
         <b>${esc(fmtDay(wk.day))} to ${esc(fmtDay(wk.last_day))}</b>${wk.complete ? "" : ` <span class="cx-tag">in progress</span>`}
         <button type="button" id="cx-wk-next" title="Next week">&rarr;</button></div>
      <div class="cx-grid2">
         <div><h3>Top players</h3>
            <table class="cd-table"><thead><tr><th>#</th><th>Player</th><th>Week XP</th><th>Award</th></tr></thead><tbody>
            ${w.players.slice(0, 8).map((p) => `<tr class="${p.prize ? "cx-win" : ""}"><td>${p.place}</td><td>${esc(p.name)}${p.squad ? `<div class="cx-sub">${esc(p.squad)}</div>` : ""}</td>
               <td class="cx-num">${num(p.week_xp)}</td><td class="cx-num">${p.prize ? `+${num(p.prize)}` : ""}</td></tr>`).join("")}</tbody></table></div>
         <div><h3>Squads</h3>
            ${w.squads.length ? `<table class="cd-table"><thead><tr><th>Squad</th><th>Week XP</th><th>Award</th></tr></thead><tbody>
            ${w.squads.map((q) => `<tr class="${q.wins ? "cx-win" : ""}"><td>${esc(q.squad)}<div class="cx-sub">${q.members.map(esc).join(", ")}</div></td>
               <td class="cx-num">${num(q.week_xp)}</td><td class="cx-num">${q.wins && w.squad_prize ? `+${num(w.squad_prize)} each` : ""}</td></tr>`).join("")}</tbody></table>`
               : `<p class="empty">No squads set (Squads tab).</p>`}
            <h3>Moved up</h3>
            ${w.moved_up.length ? `<ul class="cx-moved">${w.moved_up.map((m) => `<li><b>${esc(m.name)}</b>: level ${m.from} to ${m.to} <span class="cx-sub">${esc(m.title)}</span></li>`).join("")}</ul>`
               : `<p class="empty">Nobody moved up a level this week.</p>`}</div>
      </div>
      <div class="pr-actions">
         <button type="button" id="cx-wk-award" ${wk.complete && !w.awarded ? "" : "disabled"}>${w.awarded ? "Awarded" : "Award this week"}</button>
         <span class="msg" id="cx-wk-msg">${w.awarded ? "This week has been awarded (a week is awarded once; fix a mistake with Revoke and a coach award)."
            : wk.complete ? "" : "The week is not over yet: award it at Friday practice."}</span></div>`;
   const P = a.presets;
   body.innerHTML = `
      <p class="cx-note">Bonus XP counts toward levels, the season board and gear, like tab XP. Weekly awards do not count toward the next week's board.</p>
      <section class="cx-card"><h2>This week's awards</h2>${weekHtml}</section>
      <section class="cx-card"><h2>Coach awards</h2>
         <div class="cx-award-form">
            <label>Award <select id="cx-aw-preset">${Object.entries(P).map(([k, v]) => `<option value="${k}" data-points="${v.points}">${esc(v.label)} (+${num(v.points)})</option>`).join("")}</select></label>
            <label>XP <input type="number" id="cx-aw-points" min="1" max="1000" value="${Object.values(P)[0] ? Object.values(P)[0].points : 50}"></label>
            <label class="cx-grow">Note <input type="text" id="cx-aw-note" maxlength="200" placeholder="optional (required for Other)"></label>
         </div>
         <div class="cx-award-players"><label class="cx-check"><input type="checkbox" id="cx-aw-all"> <b>Everyone</b></label>
            ${a.players.map((p) => `<label class="cx-check"><input type="checkbox" class="cx-aw-p" value="${p.user_id}"> ${esc(p.name)}</label>`).join("")}</div>
         <div class="pr-actions"><button type="button" id="cx-aw-go">Give award</button><span class="msg" id="cx-aw-msg"></span></div>
         <p class="cx-note">Friday practice counts once per player per day, and the Friday-practice count is what the gear tiers use.</p>
         <h3>Recent awards</h3>
         ${a.awards.length ? `<div class="cx-scroll"><table class="cd-table"><thead><tr><th>When</th><th>Player</th><th>Award</th><th>XP</th><th>By</th><th></th></tr></thead><tbody>
            ${a.awards.map((x) => `<tr class="${x.revoked_at ? "cx-revoked" : ""}"><td>${esc(new Date(x.earned_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }))}</td>
               <td>${esc(x.name)}</td><td>${esc(x.detail || x.code)}</td><td class="cx-num">+${num(x.points)}</td><td>${esc(x.by_name || "")}</td>
               <td>${x.revoked_at ? "revoked" : `<button type="button" class="cx-revoke" data-id="${x.bonus_id}">Revoke</button>`}</td></tr>`).join("")}</tbody></table></div>`
            : `<p class="empty">No awards yet.</p>`}
      </section>`;
   if (w.ok) {
      body.querySelector("#cx-wk-prev").addEventListener("click", () => { this.weekDay = shift(wk.day, -7); this.renderAwards().catch((e) => this.msg(e.message, "err")); });
      body.querySelector("#cx-wk-next").addEventListener("click", () => { this.weekDay = shift(wk.day, 7); this.renderAwards().catch((e) => this.msg(e.message, "err")); });
      body.querySelector("#cx-wk-award").addEventListener("click", async () => {
         const out = body.querySelector("#cx-wk-msg");
         out.textContent = "Awarding...";
         try {
            const r = await api(`/classes/${this.classId}/xp/weekly`, { method: "POST", body: JSON.stringify({ week: wk.day }) });
            const d = await r.json();
            if (!d.ok) { out.textContent = `Not awarded (${d.reason}${d.detail ? `: ${d.detail}` : ""}).`; return; }
            await this.renderAwards();
            const again = this.querySelector("#cx-wk-msg");
            if (again) again.textContent = d.awarded ? `Awarded ${num(d.points)} XP in ${d.awarded} award${d.awarded === 1 ? "" : "s"}.` : "Nothing new to award.";
         } catch { out.textContent = "Not awarded (offline)."; }
      });
   }
   const preset = body.querySelector("#cx-aw-preset");
   preset.addEventListener("change", () => { body.querySelector("#cx-aw-points").value = preset.selectedOptions[0].dataset.points; });
   body.querySelector("#cx-aw-all").addEventListener("change", (e) => body.querySelectorAll(".cx-aw-p").forEach((x) => { x.checked = e.target.checked; }));
   body.querySelector("#cx-aw-go").addEventListener("click", async () => {
      const out = body.querySelector("#cx-aw-msg");
      const user_ids = [...body.querySelectorAll(".cx-aw-p:checked")].map((x) => Number(x.value));
      if (!user_ids.length) { out.textContent = "Tick at least one player."; return; }
      out.textContent = "Saving...";
      try {
         const r = await api(`/classes/${this.classId}/xp/awards`, { method: "POST", body: JSON.stringify({
            user_ids, preset: preset.value, points: Number(body.querySelector("#cx-aw-points").value) || null, note: body.querySelector("#cx-aw-note").value }) });
         const d = await r.json();
         if (!d.ok) { out.textContent = `Not saved (${d.reason}${d.detail ? `: ${d.detail}` : ""}).`; return; }
         await this.renderAwards();
         const again = this.querySelector("#cx-aw-msg");
         if (again) again.textContent = `Awarded +${num(d.points)} XP to ${d.awarded} player${d.awarded === 1 ? "" : "s"}.${d.already.length ? ` Already had it today: ${d.already.join(", ")}.` : ""}`;
      } catch { out.textContent = "Not saved (offline)."; }
   });
   body.querySelectorAll(".cx-revoke").forEach((b) => b.addEventListener("click", async () => {
      b.disabled = true;
      try {
         const r = await api(`/classes/${this.classId}/xp/awards/${b.dataset.id}`, { method: "DELETE" });
         const d = await r.json();
         if (!d.ok) { b.textContent = `Failed (${d.reason})`; return; }
         await this.renderAwards();
      } catch { b.disabled = false; b.textContent = "Offline"; }
   }));
};

/* ---------- Daily challenge (2026-10-21) ---------- */
AppClassXp.prototype.renderDaily = function renderDaily(d) {
   const body = this.querySelector("#cx-body");
   const tiers = (p) => (p.tier_min == null ? "" : p.tier_min === p.tier_max ? ` (T${p.tier_min})` : ` (T${p.tier_min}-${p.tier_max})`);
   body.innerHTML = `
      <p class="cx-note">Every day each player sees one challenge pack at the top of their Game Launcher. The first time they finish it that day
         they earn its tab XP again (${d.multiplier}x in all), and it counts toward their daily streak. It is picked automatically
         (${d.focus_used ? "from this week's focus skills on the Coaching tab" : "from the open packs; set a weekly focus on the Coaching tab to steer it"}), never the same pack two days running when there is a choice.
         Pin a different pack for any day; Automatic goes back to the pick.</p>
      ${d.packs.length ? "" : `<p class="msg warn">No Game Launcher pack is open in this class, so there is no challenge.</p>`}
      <table class="cd-table cx-daily"><thead><tr><th>Day</th><th>Challenge</th><th>Set by</th><th>Change</th></tr></thead><tbody>
      ${d.days.map((x, i) => `<tr class="${i === 0 ? "cx-win" : ""}"><td>${esc(fmtDay(x.day))}${i === 0 ? " <b>(today)</b>" : ""}</td>
         <td>${x.empty ? `<span class="cx-sub">none</span>` : `<b>${esc(x.title)}</b>${esc(tiers(x))}${x.closed ? ` <span class="cx-tag">closed</span>` : ""}`}</td>
         <td>${x.pinned ? "Coach" : `<span class="cx-sub">automatic</span>`}</td>
         <td><select class="cx-daily-pin" data-day="${esc(x.day)}"><option value="">Automatic</option>${d.packs.map((p) =>
            `<option value="${p.pack_id}" ${x.pinned && x.pack_id === p.pack_id ? "selected" : ""}>${esc(p.title)}${esc(tiers(p))}</option>`).join("")}</select></td></tr>`).join("")}
      </tbody></table>
      <p class="msg" id="cx-daily-msg"></p>
      <h3>Done today</h3>
      ${d.done_today.length ? `<ul class="cx-moved">${d.done_today.map((x) => `<li>${esc(x.name)} <span class="cx-sub">+${num(x.points)}</span></li>`).join("")}</ul>` : `<p class="empty">Nobody yet.</p>`}`;
   body.querySelectorAll(".cx-daily-pin").forEach((sel) => sel.addEventListener("change", async () => {
      const out = body.querySelector("#cx-daily-msg");
      out.textContent = "Saving...";
      try {
         const r = await api(`/classes/${this.classId}/xp/daily`, { method: "PUT", body: JSON.stringify({ day: sel.dataset.day, pack_id: sel.value ? Number(sel.value) : null }) });
         const x = await r.json();
         if (!x.ok) { out.textContent = `Not saved (${x.reason}${x.detail ? `: ${x.detail}` : ""}).`; return; }
         this.renderDaily(await this.get(`/classes/${this.classId}/xp/daily`));
      } catch { out.textContent = "Not saved (offline)."; }
   }));
};

/* ---------- Gear (2026-10-21, TENTATIVE) ---------- */
AppClassXp.prototype.renderGear = function renderGear(g) {
   const body = this.querySelector("#cx-body");
   if (!g.players.length) {
      body.innerHTML = `<p class="cx-note">Gear is for the <b>team</b> class (ConCats). Mark the team class on the Awards tab.</p>`;
      return;
   }
   body.innerHTML = `
      ${g.tentative ? `<p class="msg warn"><b>Tentative and preliminary:</b> these gear levels are a first draft and may change.</p>` : ""}
      <p class="cx-note">${g.tiers.map((t) => `<b>${esc(t.label)}</b>: ${num(t.xp)} XP${t.fridays ? ` + ${t.fridays} Friday practices` : ""}`).join(" · ")}.
         XP is everything a player has ever earned (tabs plus awards); Friday practices are the Friday-practice coach awards. Tick a box when the gear is handed out.</p>
      <div class="cx-scroll"><table class="cd-table cx-gear"><thead><tr><th>Player</th><th>Level</th><th>XP</th><th>Fridays</th>
         ${g.tiers.map((t) => `<th>${esc(t.label)}</th>`).join("")}<th>Next</th></tr></thead><tbody>
         ${g.players.map((p) => `<tr><td>${esc(p.name)}<div class="cx-sub">${esc(p.email)}</div></td><td>${p.level}</td><td class="cx-num">${num(p.xp)}</td><td class="cx-num">${p.fridays}</td>
            ${p.gear.tiers.map((t) => `<td class="${t.earned ? (t.handed_out ? "cx-gear-done" : "cx-gear-earned") : "cx-gear-no"}">${t.earned
               ? `<label class="cx-check"><input type="checkbox" class="cx-hand" data-uid="${p.user_id}" data-gear="${esc(t.key)}" ${t.handed_out ? "checked" : ""}> ${t.handed_out ? "handed out" : "earned"}</label>`
               : `<span class="cx-sub">not yet</span>`}</td>`).join("")}
            <td class="cx-sub">${p.gear.next ? `${esc(p.gear.next.label)}: ${[p.gear.next.xp_to_go ? `${num(p.gear.next.xp_to_go)} XP` : "", p.gear.next.fridays_to_go ? `${p.gear.next.fridays_to_go} Fridays` : ""].filter(Boolean).join(" + ") || "ready"}` : "all earned"}</td></tr>`).join("")}
      </tbody></table></div>
      <p class="msg" id="cx-gear-msg"></p>`;
   body.querySelectorAll(".cx-hand").forEach((cb) => cb.addEventListener("change", async () => {
      const out = body.querySelector("#cx-gear-msg");
      try {
         const r = await api(`/classes/${this.classId}/xp/gear`, { method: "PUT", body: JSON.stringify({ user_id: Number(cb.dataset.uid), gear: cb.dataset.gear, handed_out: cb.checked }) });
         const d = await r.json();
         if (!d.ok) { cb.checked = !cb.checked; out.textContent = `Not saved (${d.reason}).`; return; }
         this.renderGear(await this.get(`/classes/${this.classId}/xp/gear`));
      } catch { cb.checked = !cb.checked; out.textContent = "Not saved (offline)."; }
   }));
};

customElements.define("app-class-xp", AppClassXp);
